import { FoodDataSource } from "../../types";

export type IngredientSourceChipLabel = "USDA" | "CoFID" | "NUTTB" | "TWFDB" | "MANUAL" | "Branded" | "Recipe";

export type IngredientSourceChipColor = "grey" | "green" | "amber" | "blue" | "purple";

export interface IngredientSourceChipInfo {
  label: IngredientSourceChipLabel;
  color: IngredientSourceChipColor;
  tooltip?: string;
}

/** Placeholder until product copy lands. */
const TWFDB_TOOLTIP = "Compiled by Teamworks from trusted food databases.";
/** Placeholder until product copy lands. */
const MANUAL_TOOLTIP = "Custom foods added by your organization.";

export type FoodOrRecipeForIngredientSource = {
  __typename: string;
  source?: FoodDataSource | null;
  org?: { id: string } | null;
  orgGroup?: { id: string } | null;
};

/**
 * Maps a food/recipe to the redesign ingredient source chip.
 * Recipe = added from the Recipes tab.
 * Branded = Passio/Nutritionix, added from the Branded tab.
 * Foods-tab results use the remaining labels: USDA, CoFID, NUTTB, TWFDB, MANUAL.
 * TWFDB = manual food with no org and no org group (TW Nutrition Staff > Food Management).
 * MANUAL = org or org-group manual food.
 */
const isFoodDataSource = (value: unknown): value is FoodDataSource =>
  value === "manual" ||
  value === "usda" ||
  value === "nutritionix" ||
  value === "bonAppetit" ||
  value === "compass" ||
  value === "sodexo" ||
  value === "cbord" ||
  value === "restaurant" ||
  value === "mccance" ||
  value === "passio" ||
  value === "ausnut";

const readOrgGroupId = (foodOrRecipe: object): { id: string } | null => {
  if (!("orgGroup" in foodOrRecipe) || foodOrRecipe.orgGroup == null || typeof foodOrRecipe.orgGroup !== "object") {
    return null;
  }
  if (!("id" in foodOrRecipe.orgGroup) || typeof foodOrRecipe.orgGroup.id !== "string") {
    return null;
  }
  return { id: foodOrRecipe.orgGroup.id };
};

/** Reads source fields when the active query fetched IngredientSourceFood. Absent fields yield no chip. */
export const getIngredientSourceChipFromFoodOrRecipe = (foodOrRecipe: {
  __typename: string;
  org?: { id: string } | null;
  source?: unknown;
  orgGroup?: unknown;
}): IngredientSourceChipInfo | null => {
  if (foodOrRecipe.__typename === "Recipe") {
    return getIngredientSourceChip({ __typename: foodOrRecipe.__typename });
  }
  if (!("source" in foodOrRecipe) || !isFoodDataSource(foodOrRecipe.source)) {
    return null;
  }
  return getIngredientSourceChip({
    __typename: foodOrRecipe.__typename,
    source: foodOrRecipe.source,
    org: foodOrRecipe.org,
    orgGroup: readOrgGroupId(foodOrRecipe),
  });
};

export const getIngredientSourceChip = (foodOrRecipe: FoodOrRecipeForIngredientSource): IngredientSourceChipInfo | null => {
  if (foodOrRecipe.__typename === "Recipe") {
    return { label: "Recipe", color: "purple" };
  }

  const { source } = foodOrRecipe;
  if (!source) {
    return null;
  }

  switch (source) {
    case "usda":
      return { label: "USDA", color: "grey" };
    case "mccance":
      return { label: "CoFID", color: "grey" };
    case "ausnut":
      return { label: "NUTTB", color: "grey" };
    case "manual": {
      const isStaffFoodManagement = !foodOrRecipe.org && !foodOrRecipe.orgGroup;
      return isStaffFoodManagement
        ? { label: "TWFDB", color: "green", tooltip: TWFDB_TOOLTIP }
        : { label: "MANUAL", color: "amber", tooltip: MANUAL_TOOLTIP };
    }
    case "nutritionix":
    case "passio":
      return { label: "Branded", color: "blue" };
    default:
      return null;
  }
};
